import React, { useEffect } from "react";

import type { SxProps } from "@mui/material";

import PlaybackOverlay, {
  PlaybackOverlayButton,
} from "@app/analytics/components/PlaybackOverlay";
import PlayerControls from "@app/analytics/components/PlayerControls";
import VideoIconButton from "@app/analytics/components/VideoIconButton";
import useHlsVideo from "@app/analytics/hooks/useHlsVideo";
import useVideoClip from "@app/analytics/hooks/useVideoClip";
import type { VideoClip } from "@app/analytics/types";
import type { VideoRecording } from "@app/common/types";

import {
  StyledVideo,
  Thumbnail,
  VideoBackdrop,
  VideoContainer,
  VideoControlsContainer,
  VideoInnerContainer,
  VideoOuterContainer,
} from "./styles";

export interface ExpandableVideoProps {
  clip: VideoClip;
  containerSx?: SxProps;
  forcePause?: boolean;
  forcePauseOnMinimize?: boolean;
  maximizeOnPlay?: "always" | "on_start" | "never";
  onPlay?: (clipId: string) => void;
  recordingInfo: VideoRecording;
  /** The overlay that will render by default; when the video is paused and minimized */
  renderMinimizedOverlay?: (props: {
    onClick: React.MouseEventHandler<HTMLElement>;
    duration: number;
  }) => React.ReactNode;
  thumbnailUrl?: string;
  useHlsDuration?: boolean;
}

const ExpandableVideo = ({
  clip,
  containerSx,
  forcePause,
  forcePauseOnMinimize = false,
  maximizeOnPlay = "always",
  onPlay,
  recordingInfo,
  renderMinimizedOverlay,
  thumbnailUrl,
  useHlsDuration = false,
}: ExpandableVideoProps) => {
  const { video, videoRef, hlsDuration } = useHlsVideo(recordingInfo, {
    startTimestamp: clip.start,
  });

  const {
    currentTime,
    controls,
    onActionClick,
    onScrubberChange,
    onVideoClick,
    played,
  } = useVideoClip(video, clip, {
    onPlay,
    maximizeOnPlay,
    pauseOnMinimize: forcePauseOnMinimize,
  });

  useEffect(() => {
    if (forcePause) controls.pause();
  }, [forcePause]);

  const handleBackdropClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    controls.minimize();
    controls.pause();
    controls.jumpToTimestamp(clip.start);
  };

  const handleMinimize: React.MouseEventHandler<HTMLElement> = (e) => {
    if (forcePauseOnMinimize) {
      handleBackdropClick(e);
    } else {
      controls.minimize();
    }
  };

  return (
    <VideoOuterContainer expanded={controls.expanded} sx={containerSx}>
      {controls.expanded && <VideoBackdrop onClick={handleBackdropClick} />}
      <VideoInnerContainer expanded={controls.expanded} onClick={onVideoClick}>
        <VideoContainer>
          {thumbnailUrl && !controls.expanded && (
            <Thumbnail
              sx={{
                minHeight: "100%",
                width: "100%",
                backgroundImage: `url("${thumbnailUrl}")`,
              }}
            />
          )}
          <StyledVideo
            poster={thumbnailUrl}
            id={`video-moment-${clip.start}-${clip.end}`}
            ref={videoRef}
            preload="auto"
          />
          {controls.paused ? (
            controls.expanded || !renderMinimizedOverlay ? (
              <PlaybackOverlay
                onClick={controls.expanded ? undefined : onVideoClick}
              >
                <PlaybackOverlayButton
                  icon={played ? "replay" : "play"}
                  onClick={onVideoClick}
                />
                {controls.expanded && (
                  <PlaybackOverlayButton
                    icon="minimize"
                    onClick={handleMinimize}
                  />
                )}
              </PlaybackOverlay>
            ) : (
              renderMinimizedOverlay({
                onClick: onVideoClick,
                duration: Math.floor(
                  useHlsDuration ? hlsDuration : clip.duration
                ),
              })
            )
          ) : (
            <VideoControlsContainer>
              {controls.expanded ? (
                <PlayerControls
                  currentTime={currentTime}
                  duration={useHlsDuration ? hlsDuration : clip.duration}
                  muted={controls.muted}
                  onActionClick={onActionClick}
                  onScrubberChange={onScrubberChange}
                  paused={controls.paused}
                  expandable
                  expanded
                />
              ) : (
                <VideoIconButton
                  icon="maximize"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onActionClick("maximize");
                  }}
                  sx={{ bgcolor: "#0000008D" }}
                />
              )}
            </VideoControlsContainer>
          )}
        </VideoContainer>
      </VideoInnerContainer>
    </VideoOuterContainer>
  );
};

export default ExpandableVideo;
